import {
  BaseResponse,
  parseErrorContainer,
  type ItemsResponse,
} from "../../tastyworks"
import type HttpClient from "../../tastyworks/http"
import { ORDER_STATUS_DESER, OrderStatus } from "../../tastyworks/order-status"
import type { JsonHelper } from "../../tastyworks/util/json"

export class AccountOrdersService {
  constructor(private readonly accountNumberClient: HttpClient) {}

  /**
   * Orders for the account — `GET /accounts/{n}/orders` — optionally narrowed by
   * `instrument_type` and one or more `status` values (e.g. Event Contract + the
   * working statuses). `id` and `account-number` bind per item (OrderStatus
   * constructor args), mirroring AdminOrdersService.parseOrdersList.
   */
  readonly getOrders = async (
    accountNumber: string,
    {
      instrumentType,
      status,
    }: { instrumentType?: string; status?: string[] } = {}
  ): Promise<ItemsResponse<OrderStatus>> =>
    this.accountNumberClient.indexSimple(
      (helper) =>
        ORDER_STATUS_DESER.toParser(
          OrderStatus.bind(
            "",
            helper.getString("id"),
            helper.getString("account-number")
          )
        )(helper),
      "orders",
      {
        accountNumber,
        ...(instrumentType ? { instrument_type: instrumentType } : {}),
        ...((status?.length ?? 0) > 0 ? { status } : {}),
      }
    )

  readonly create = async (
    accountNumber: string,
    payload: Record<string, unknown>
  ): Promise<JsonHelper> =>
    this.accountNumberClient.postJson("orders", payload, {
      accountNumber,
    })

  readonly dryRun = async (
    accountNumber: string,
    payload: Record<string, unknown>
  ): Promise<JsonHelper> =>
    this.accountNumberClient.postJson("orders/dry-run", payload, {
      accountNumber,
    })

  /**
   * Cancels a working order — `DELETE /accounts/{n}/orders/{orderId}`. The
   * backend owns cancel eligibility (ATP-14835); callers rely on the response
   * (and the live Order-streamer drop) rather than gating client-side.
   */
  readonly cancel = async (
    accountNumber: string,
    orderId: string
  ): Promise<BaseResponse> => {
    const helper = await this.accountNumberClient.doDelete(
      `orders/${orderId}`,
      { accountNumber }
    )
    // Cancel returns no body we consume — only the error envelope matters, so a
    // plain BaseResponse (what parseErrorContainer populates) is the honest type.
    const response = new BaseResponse()
    parseErrorContainer(helper, response)
    return response
  }
}
